import { REJECT, nullable, num, object, str, unknownRecord, type FieldParser, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';
import type { EventAction, EventConfig } from 'redux/slices/eventsSlice';

// `config` and `action` are per-event-type dynamic JSON: the server is the
// validation authority for their internals and the modal reads them defensively
// (optional chaining + per-field fallbacks). Validated only as "an object or
// null" — contents pass through verbatim, typed as the modal's shapes.
//
// NULLABLE, not defaulted-to-`{}`: `routes/events.js` forces both to null for a
// `processEvent` (`resolveEventRequestConfig`), and that is the stored row, so
// null is the documented shape for a whole event type rather than drift. Reading
// it as drift marked every process event's response degraded, which the edit
// dialog then refused to open on.
//
// The cast is an ASSERTION, not a check: `T` is whatever the caller names, and
// only "an object or null" is validated. So `EventConfig` / `EventAction` are
// claims the boundary does not keep, and the editor's optional chaining and
// per-field fallbacks are load-bearing rather than defensive — unknown values
// reach the edit controls typed as strings, numbers and booleans. Declaring
// their real shapes belongs with the events M pass.
const dynamicJson = <T>(): FieldParser<T | null> => nullable(unknownRecord()) as FieldParser<T | null>;

/**
 * A single event as returned by nap-backend `GET /events/:id` (routes/events.js
 * — `SELECT e.*, zone_name` + `folder_path`). This is the edit-relevant
 * projection re-derived from the backend source; the endpoint also returns
 * `zone_id`, `created_at`, `updated_at`, `audit_data` and `folder_path`, which
 * the modal does not consume, so the object parser drops them. `description` is
 * coerced to `string` (the wire column is nullable; the form binds it to an
 * input where `''` is the natural empty). Assignable to the slice `Event` shape
 * the edit modal accepts. Deeply readonly — validated wire data.
 */
export interface FetchedEvent {
  readonly event_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly zone_name: string;
  readonly folder_id: number | null;
  readonly config: EventConfig | null;
  readonly action: EventAction | null;
}

const eventSchema = object<FetchedEvent>({
  event_id: num(REJECT),
  name: str(),
  description: nullable(str()),
  type: str(),
  zone_name: str(),
  folder_id: nullable(num()),
  config: dynamicJson<EventConfig>(),
  action: dynamicJson<EventAction>(),
});

/**
 * GET `/events/:id` — one record for the edit dialog, so it works
 * when launched from the Workspace (which never populates the list slice) and
 * never opens on a loosely-cast store row.
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short
 * version is that `null` means the server said there is no such record, and
 * the failure arm means it said something we could not read. The caller must
 * tell those apart, because refreshing a list helps with only one of them.
 */
export const getEvent = (id: number): Promise<Received<FetchedEvent | null>> => getRecord(`/events/${id}`, { schema: eventSchema, source: 'getEvent' });
